import { useNavigate, useParams } from "react-router";
import { AlertCircle, Trash2 } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import { eftHashPath } from "@cle/constants/routes";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import RoundedCard from "@cle/components/RoundedCard";
import {
  useDeleteInsurancePolicy,
  useInsurancePolicyDocument,
  useInsurancePolicyDocumentFile,
} from "@/hooks/useInsurancePolicies";
import {
  getPolicyStatusLabel,
  isPolicyAnalyzingStatus,
  isPolicyApprovedStatus,
  isPolicyAwaitingReviewStatus,
  isPolicyRejectedByAiStatus,
  isPolicyRejectedStatus,
  type PolicyStatus,
} from "@cle/models/policyStatus";
import { formatDanishLongDate } from "@/util/date";
import { createInsurancePolicyValidationItems } from "@cle/utils/insurancePolicyValidation";
import { allFieldsApproved } from "@cle/utils/fieldValidationHelpers";
import DocumentStatusBadge from "./components/DocumentStatusBadge";
import DocumentValidationCard from "./components/DocumentValidationCard";
import InsurancePolicyValidationInfoContent from "./components/InsurancePolicyValidationInfoContent";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import UploadedDocumentPreviewCard from "./components/UploadedDocumentPreviewCard";

const MISSING_VALUE = "-";

const getStatusVariant = (
  status: PolicyStatus,
): "danger" | "info" | "success" | "warning" => {
  if (isPolicyApprovedStatus(status)) {
    return "success";
  }

  if (isPolicyAwaitingReviewStatus(status)) {
    return "warning";
  }

  if (isPolicyRejectedStatus(status) || status === "failed") {
    return "danger";
  }

  return "info";
};

const InsurancePolicyDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: insurancePolicy,
    isError: isInsurancePolicyError,
    isLoading: isInsurancePolicyLoading,
  } = useInsurancePolicyDocument(id);
  const {
    data: documentFile,
    isError: isDocumentFileError,
    isLoading: isDocumentFileLoading,
  } = useInsurancePolicyDocumentFile(id);
  const deleteInsurancePolicyMutation = useDeleteInsurancePolicy();
  const policyTitle = insurancePolicy?.fileName ?? "Forsikring.pdf";
  const isReported = insurancePolicy?.reported ?? false;
  const isDeleteDisabled =
    id.length === 0 || deleteInsurancePolicyMutation.isPending || isReported;
  const policyStatus = insurancePolicy?.status;
  const isAnalyzing = policyStatus
    ? isPolicyAnalyzingStatus(policyStatus)
    : false;
  const validationItems = createInsurancePolicyValidationItems(insurancePolicy);
  const statusLabel = insurancePolicy
    ? getPolicyStatusLabel(policyStatus) ||
      (isAnalyzing ? "Analyseres" : MISSING_VALUE)
    : isInsurancePolicyLoading
      ? "Indlæser"
      : MISSING_VALUE;

  const handleDeleteInsurancePolicy = (closeConfirmation: () => void) => {
    deleteInsurancePolicyMutation.mutate(id, {
      onSuccess: () => {
        closeConfirmation();
        void navigate(eftHashPath("policies"));
      },
    });
  };

  return (
    <div className="cle-course-certificate-document">
      {isInsurancePolicyError ? (
        <div className="alert alert-warning" role="alert">
          Forsikringspolicens oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {deleteInsurancePolicyMutation.isError ? (
        <div className="alert alert-warning" role="alert">
          Forsikringspolicen kunne ikke slettes lige nu. Prøv igen.
        </div>
      ) : null}
      {insurancePolicy && isPolicyRejectedByAiStatus(insurancePolicy.status) ? (
        <div
          style={{ position: "relative" }}
          className="border rounded-3 overflow-hidden fs-6 fw-semibold mb-4 bg-white p-4 d-flex align-items-center gap-2"
          role="alert"
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "0.25rem",
              height: "100%",
              backgroundColor: "#c43333",
            }}
          />
          <AlertCircle size={26} aria-hidden style={{ color: "#c43333" }} />
          <div className="d-flex flex-column">
            <p className="mb-0">
              Forsikringspolicen er blevet afvist af systemet.
            </p>
            <p style={{ fontSize: "0.85rem" }} className="mb-0 opacity-75">
              {allFieldsApproved(insurancePolicy.fieldValidation)
                ? "Oplysningerne opfylder nu kravene, men afgørelsen er ikke opdateret. Kontakt sekretariatet længere nede på siden, hvis policen fortsat står som afvist."
                : "Se i valideringen herunder, hvilke krav der ikke er opfyldt. Mener du, at det er en fejl, kan du sende en besked til sekretariatet længere nede på siden."}
            </p>
          </div>
        </div>
      ) : null}

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <UploadedDocumentPreviewCard
              certificateTitle={policyTitle}
              fileBlob={documentFile}
              isError={isDocumentFileError}
              isLoading={isDocumentFileLoading}
            />
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Information"
              action={
                <ConfirmationModal
                  title="Slet forsikringspolice"
                  description="Er du sikker på, at du vil slette forsikringspolicen? Handlingen kan ikke fortrydes."
                  confirmLabel="Slet"
                  confirmVariant="destructive"
                  confirmButtonId="cle-confirm-delete-insurance-policy"
                  cancelButtonId="cle-cancel-delete-insurance-policy"
                  isConfirming={deleteInsurancePolicyMutation.isPending}
                  isConfirmDisabled={isDeleteDisabled}
                  onConfirm={({ close }) => handleDeleteInsurancePolicy(close)}
                  trigger={
                    <CleButton
                      id="cle-delete-insurance-policy"
                      variant="destructive"
                      size="sm"
                      disabled={isDeleteDisabled}
                    >
                      <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                      {deleteInsurancePolicyMutation.isPending
                        ? "Sletter..."
                        : "Slet"}
                    </CleButton>
                  }
                >
                  <p className="mb-0">
                    Forsikringspolicen og det uploadede dokument slettes
                    permanent.
                  </p>
                </ConfirmationModal>
              }
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Dokument</dt>
                <dd>{policyTitle}</dd>

                <dt>Type</dt>
                <dd>Forsikring og garanti</dd>

                <dt>Status</dt>
                <dd className="d-flex flex-wrap gap-2">
                  <DocumentStatusBadge
                    variant={
                      policyStatus ? getStatusVariant(policyStatus) : "info"
                    }
                  >
                    {statusLabel}
                  </DocumentStatusBadge>
                  {isReported ? (
                    <DocumentStatusBadge variant="primary">
                      Indberettet
                    </DocumentStatusBadge>
                  ) : null}
                </dd>

                <dt>Uploadet den</dt>
                <dd>
                  {formatDanishLongDate(
                    insurancePolicy?.uploadedAt,
                    MISSING_VALUE,
                  )}
                </dd>
              </dl>
            </RoundedCard>

            <DocumentValidationCard
              isAnalyzing={isAnalyzing}
              items={validationItems}
              analyzingTitle="Forsikringspolicen analyseres"
              analyzingDescription="Dokumentet gennemgås automatisk. Det kan tage op til 2 minutter, før valideringen er færdig."
              infoModalTitle="Validering af forsikringspolice"
              infoModalDescription="Sådan læser du valideringen af en forsikringspolice"
              infoModalAriaLabel="Læs mere om validering af forsikringspolice"
              infoModalContent={<InsurancePolicyValidationInfoContent />}
            />
            <SecretariatMessageCard subjectId={id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsurancePolicyDocumentView;
